import React, { useEffect, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { LandingPageLayout, SNotification } from '../../../lib/landing-page-layout'
import acceptInvitation from '../../../lib/acceptInvitation'
import useDefer, { Status } from '../../../common/hooks/useDefer'
import Text from '../../../ui/Text'
import { FormHeader } from '../../../lib/form-header'
import useText from '../../../common/hooks/useText'

const Container = styled.div`
    width: 440px;
    font-family: 'Lateral';
    font-size: 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 10px;
`

const ReturnButton = styled.div`
    color: ${({ theme }) => theme.font.midDarkColor};
    font-weight: 400;
    font-size: 16px;

    &:hover {
        cursor: pointer;
    }
`

export type AcceptInvitationPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

export function AcceptInvitationPage({ className, style }: AcceptInvitationPageProps): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [message, setMessage] = useState<{ type: 'success' | 'error' | undefined; message: string }>({
        type: undefined,
        message: '',
    })

    const acceptInvitationRequest = useDefer((token: string) => acceptInvitation(token))

    useEffect(() => {
        const token = searchParams.get('token')

        if (token) {
            acceptInvitationRequest.execute(token).catch(() => {})
        } else {
            setMessage({ type: 'error', message: text('msgInviteNotAccepted') })
        }
    }, [])

    useEffect(() => {
        if (acceptInvitationRequest.status === Status.SUCCESS)
            setMessage({ type: 'success', message: 'msgInviteAccepted' })
        if (acceptInvitationRequest.status === Status.ERROR)
            setMessage({ type: 'error', message: 'msgInviteNotAccepted' })
    }, [acceptInvitationRequest.status])

    return (
        <LandingPageLayout className={cn(className)} style={style}>
            <Container>
                <FormHeader />

                {message.type && (
                    <SNotification error={message.type === 'error'}>
                        <Text code={message.message} />
                    </SNotification>
                )}

                <ReturnButton onClick={() => navigate('/login')}>
                    <Text code="btnReturnToSignIn" />
                </ReturnButton>
            </Container>
        </LandingPageLayout>
    )
}
